import React, { forwardRef } from 'react'
import { Dialog, Slide, Fade } from '@mui/material'
import PropTypes from 'prop-types'
import { useCallback, useMemo, useState, isValidElement } from 'react'

import { styles, handleStylesWithProps } from './dialog-layout/dialog-layout.styles'
import { DialogContext } from './dialog.context'

const INITIAL_STATE = {
  dialogState: {},
  dialogStack: [],
}

const Transition = forwardRef(function Transition(props, ref) {
  const { dialogStack, ...transitionProps } = props
  const dialogToBeRendered = dialogStack?.[dialogStack?.length - 1]
  const slideDirection = dialogToBeRendered?.props?.animation
  const shouldUseFadeAnimation = !slideDirection

  return shouldUseFadeAnimation ? (
    <Fade timeout={400} ref={ref} {...transitionProps} />
  ) : (
    <Slide
      appear={true}
      direction={slideDirection}
      in={props.in}
      mountOnEnter
      unmountOnExit
      ref={ref}
      {...transitionProps}
      timeout={350}
    />
  )
})

Transition.propTypes = {
  dialogStack: PropTypes.array,
  in: PropTypes.bool,
}

export const DialogProvider = ({ children, component, open, ...props }) => {
  const [state, setState] = useState(INITIAL_STATE)
  const { dialogState, dialogStack } = state

  const dialogToBeRendered = dialogStack?.[dialogStack?.length - 1]

  const dialogProps = {
    ...dialogToBeRendered?.props,
    ...props,
  }

  const DialogComponent = dialogToBeRendered?.component || component

  const renderDialogComponent = () => {
    if (DialogComponent) {
      return isValidElement(DialogComponent) ? (
        DialogComponent
      ) : (
        <DialogComponent {...dialogProps} />
      )
    }
    return null
  }

  const stylesWithProps = handleStylesWithProps({
    size: dialogState?.size,
    hasMobileBottomSheet: dialogState?.hasMobileBottomSheet,
  })

  const closeDialog = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      dialogState: { ...prevState.dialogState, open: false },
    }))
  }, [])

  const isClosing = useMemo(() => {
    return dialogStack.length > 0 && !dialogState?.open && !open
  }, [dialogState?.open, open, dialogStack.length])

  const handleExited = useCallback(() => {
    if (!dialogState?.open && !open) {
      requestAnimationFrame(() => {
        setState(INITIAL_STATE)
      })
    }
  }, [dialogState?.open, open])

  const value = useMemo(() => {
    return [state, setState, closeDialog]
  }, [state, closeDialog, setState])

  return (
    <DialogContext.Provider value={value}>
      {children}
      {(dialogState?.open || open || isClosing) && (
        <Dialog
          disableEnforceFocus={true}
          disableRestoreFocus={true}
          open={dialogState?.open || open}
          onClose={dialogState?.closeDialog || closeDialog}
          TransitionComponent={Transition}
          TransitionProps={{
            dialogStack,
            onExited: handleExited,
          }}
          PaperProps={{
            sx: {
              ...styles.paper,
              ...stylesWithProps.paper,
              ...dialogState?.stylesOverride,
            },
          }}
        >
          {renderDialogComponent()}
        </Dialog>
      )}
    </DialogContext.Provider>
  )
}

DialogProvider.propTypes = {
  children: PropTypes.node,
  component: PropTypes.node,
  open: PropTypes.bool,
}
DialogProvider.defaultProps = {
  open: false,
}
